import { useEffect, useState } from "react";
import { customerApi, type CustomerSettings } from "../../../api/customers";
import { locationApi, type LocationOption } from "../../../api/locations";

interface CreateCustomerModalProps {
  onClose: () => void;
  onCustomerCreated: (customer: any) => void;
}

interface ContactPerson {
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

interface CustomerSite {
  isBillingSite: boolean;
  isShippingSite: boolean;
  addressTypeId: string;
  addressLine1: string;
  countryId: string;
  stateId: string;
  districtId: string;
  cityId: string;
  stateList: LocationOption[];
  districtList: LocationOption[];
  cityList: LocationOption[];
  contactPersonInfos: ContactPerson[];
}

const createBlankContact = (): ContactPerson => ({
  firstName: "",
  lastName: "",
  phoneNumber: "",
});

const createBlankSite = (): CustomerSite => ({
  isBillingSite: false,
  isShippingSite: false,
  addressTypeId: "",
  addressLine1: "",
  countryId: "",
  stateId: "",
  districtId: "",
  cityId: "",
  stateList: [],
  districtList: [],
  cityList: [],
  contactPersonInfos: [],
});

export default function CreateCustomerModal({ onClose, onCustomerCreated }: CreateCustomerModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    emailAddress: "",
    customerTypeId: "",
  });
  const [customerTypes, setCustomerTypes] = useState<Array<{ id: number; name: string }>>([]);
  const [addressTypes, setAddressTypes] = useState<Array<{ id: number; name: string }>>([]);
  const [countries, setCountries] = useState<LocationOption[]>([]);
  const [customerSettings, setCustomerSettings] = useState<CustomerSettings>({
    isPhoneNumberMandatory: false,
    isEmailMandatory: false,
  });
  const [includeAddress, setIncludeAddress] = useState(false);
  const [sites, setSites] = useState<CustomerSite[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const loadInitialData = async () => {
      const [types, addrTypes, countryList, settings] = await Promise.all([
        customerApi.getCustomerTypes(),
        customerApi.getAddressTypes(),
        locationApi.getChildren(1, null),
        customerApi.getCustomerSettings(),
      ]);
      setCustomerTypes(types);
      setAddressTypes(
        addrTypes.length
          ? addrTypes
          : [
              { id: 1, name: "Home" },
              { id: 2, name: "Office" },
            ]
      );
      setCountries(countryList);
      setCustomerSettings(settings);
    };
    loadInitialData();
  }, []);

  const formatPhone = (value: string) => value.replace(/[^\d+]/g, "").slice(0, 15);

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      nextErrors.name = "Enter a customer name";
    }
    if (customerSettings.isPhoneNumberMandatory && !formData.phoneNumber.trim()) {
      nextErrors.phoneNumber = "Enter a phone number";
    }
    if (customerSettings.isEmailMandatory && !formData.emailAddress.trim()) {
      nextErrors.emailAddress = "Enter an email address";
    } else if (formData.emailAddress && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      nextErrors.emailAddress = "Enter a valid email";
    }

    if (includeAddress) {
      sites.forEach((site, index) => {
        if (!site.addressTypeId) {
          nextErrors[`site-${index}-addressTypeId`] = "Select address type";
        }
        if (!site.addressLine1.trim()) {
          nextErrors[`site-${index}-addressLine1`] = "Enter address";
        }
      });
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const setSiteValue = (index: number, patch: Partial<CustomerSite>) => {
    setSites((prev) => prev.map((site, i) => (i === index ? { ...site, ...patch } : site)));
  };

  const handleCountryChange = async (index: number, countryId: string) => {
    setSiteValue(index, {
      countryId,
      stateId: "",
      districtId: "",
      cityId: "",
      stateList: [],
      districtList: [],
      cityList: [],
    });
    const id = Number(countryId);
    if (!id) return;
    const stateList = await locationApi.getChildren(2, id);
    setSiteValue(index, { stateList });
  };

  const handleStateChange = async (index: number, stateId: string) => {
    setSiteValue(index, {
      stateId,
      districtId: "",
      cityId: "",
      districtList: [],
      cityList: [],
    });
    const id = Number(stateId);
    if (!id) return;
    const districtList = await locationApi.getChildren(3, id);
    setSiteValue(index, { districtList });
  };

  const handleDistrictChange = async (index: number, districtId: string) => {
    setSiteValue(index, {
      districtId,
      cityId: "",
      cityList: [],
    });
    const id = Number(districtId);
    if (!id) return;
    const cityList = await locationApi.getChildren(4, id);
    setSiteValue(index, { cityList });
  };

  const addCustomerSite = () => setSites((prev) => [...prev, createBlankSite()]);
  const removeCustomerSite = (index: number) =>
    setSites((prev) => prev.filter((_, i) => i !== index));

  const addContactPerson = (siteIndex: number) => {
    setSites((prev) =>
      prev.map((site, i) =>
        i === siteIndex
          ? { ...site, contactPersonInfos: [...site.contactPersonInfos, createBlankContact()] }
          : site
      )
    );
  };

  const removeContactPerson = (siteIndex: number, contactIndex: number) => {
    setSites((prev) =>
      prev.map((site, i) =>
        i === siteIndex
          ? {
              ...site,
              contactPersonInfos: site.contactPersonInfos.filter((_, idx) => idx !== contactIndex),
            }
          : site
      )
    );
  };

  const setContactValue = (
    siteIndex: number,
    contactIndex: number,
    field: keyof ContactPerson,
    value: string
  ) => {
    setSites((prev) =>
      prev.map((site, i) =>
        i === siteIndex
          ? {
              ...site,
              contactPersonInfos: site.contactPersonInfos.map((contact, idx) =>
                idx === contactIndex ? { ...contact, [field]: value } : contact
              ),
            }
          : site
      )
    );
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const selectedTypeId = Number(formData.customerTypeId);
      const isValidCustomerType = customerTypes.some((type) => type.id === selectedTypeId);
      const payload = {
        name: formData.name.trim(),
        phoneNumber: formData.phoneNumber.trim() || undefined,
        emailAddress: formData.emailAddress.trim() || undefined,
        customerTypeId: isValidCustomerType ? selectedTypeId : undefined,
        currencyId: 1,
        customerSites: includeAddress
          ? sites.map((site) => ({
              isBillingSite: site.isBillingSite,
              isShippingSite: site.isShippingSite,
              customerSiteAddress: {
                addressTypeId: Number(site.addressTypeId),
                addressLine1: site.addressLine1.trim(),
                countryId: Number(site.countryId) || null,
                stateId: Number(site.stateId) || null,
                districtId: Number(site.districtId) || null,
                cityId: Number(site.cityId) || null,
              },
              contactPersonInfos: site.contactPersonInfos
                .filter((person) => person.firstName.trim())
                .map((person) => ({
                  firstName: person.firstName.trim(),
                  lastName: person.lastName.trim() || null,
                  phoneNumber: person.phoneNumber.trim() || null,
                })),
            }))
          : [],
      };

      const customerId = await customerApi.create(payload);
      onCustomerCreated({
        id: customerId,
        name: payload.name,
        phoneNumber: payload.phoneNumber || null,
        emailAddress: payload.emailAddress || null,
      });
      onClose();
    } catch (error: any) {
      setSubmitError(error?.message || "Failed to create customer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] min-h-[300px] overflow-hidden flex flex-col">
        <div className="px-7 py-5 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-3xl font-medium text-sky-900">Add Customer</h2>
          <button onClick={onClose} disabled={isSubmitting} className="text-sky-900 hover:opacity-70">
            <i className="ri-close-line text-2xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6 bg-white">
          <h3 className="text-xl font-semibold text-sky-900">Basic Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-sky-900 mb-2">
                Customer Name <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                className={`w-full px-3 py-3 text-base border rounded-lg ${
                  errors.name ? "border-red-400" : "border-sky-200"
                }`}
                placeholder="Enter Customer Name"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-sky-900 mb-2">
                Phone Number
                {customerSettings.isPhoneNumberMandatory && <span className="text-red-500"> *</span>}
              </label>
              <input
                value={formData.phoneNumber}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, phoneNumber: formatPhone(e.target.value) }))
                }
                className={`w-full px-3 py-3 text-base border rounded-lg ${
                  errors.phoneNumber ? "border-red-400" : "border-sky-200"
                }`}
                placeholder="Enter Phone Number"
              />
              {errors.phoneNumber && <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-sky-900 mb-2">
                Email
                {customerSettings.isEmailMandatory && <span className="text-red-500"> *</span>}
              </label>
              <input
                value={formData.emailAddress}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, emailAddress: e.target.value }))
                }
                className={`w-full px-3 py-3 text-base border rounded-lg ${
                  errors.emailAddress ? "border-red-400" : "border-sky-200"
                }`}
                placeholder="Enter Email Address"
              />
              {errors.emailAddress && (
                <p className="text-red-500 text-sm mt-1">{errors.emailAddress}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-sky-900 mb-2">Customer Type</label>
              <select
                value={formData.customerTypeId}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, customerTypeId: e.target.value }))
                }
                disabled={!customerTypes.length}
                className="w-full px-3 py-3 text-base border border-sky-200 rounded-lg bg-white"
              >
                <option value="">
                  {customerTypes.length ? "Select Customer Type" : "No customer type found"}
                </option>
                {customerTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                setIncludeAddress((prev) => {
                  const next = !prev;
                  if (next && !sites.length) setSites([createBlankSite()]);
                  return next;
                });
              }}
              className={`relative inline-flex h-8 w-16 items-center rounded-full border transition ${
                includeAddress ? "bg-sky-200 border-sky-400" : "bg-gray-200 border-gray-300"
              }`}
            >
              <span
                className={`inline-block h-7 w-7 transform rounded-full bg-white transition ${
                  includeAddress ? "translate-x-8" : "translate-x-1"
                }`}
              />
            </button>
            <span className="text-lg font-semibold text-sky-900">Add Address</span>
          </div>

          {includeAddress && (
            <div className="space-y-5">
              {sites.map((site, index) => (
                <div key={index} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-sky-900">Customer Contact Address - {index + 1}</h4>
                    {sites.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCustomerSite(index)}
                        className="text-red-500 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="flex gap-8">
                    <label className="inline-flex items-center gap-2 text-base text-sky-900">
                      <input
                        type="checkbox"
                        checked={site.isBillingSite}
                        onChange={(e) => setSiteValue(index, { isBillingSite: e.target.checked })}
                      />
                      Billing Address
                    </label>
                    <label className="inline-flex items-center gap-2 text-base text-sky-900">
                      <input
                        type="checkbox"
                        checked={site.isShippingSite}
                        onChange={(e) => setSiteValue(index, { isShippingSite: e.target.checked })}
                      />
                      Shipping Address
                    </label>
                  </div>

                  <div className="bg-slate-100 rounded-lg p-5 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-sky-900 mb-2">
                        Address Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={site.addressTypeId}
                        onChange={(e) => setSiteValue(index, { addressTypeId: e.target.value })}
                        className="w-full px-3 py-3 text-base border border-sky-200 rounded-lg bg-white"
                      >
                        <option value="">Enter Address Type</option>
                        {addressTypes.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                      {errors[`site-${index}-addressTypeId`] && (
                        <p className="text-red-500 text-sm mt-1">{errors[`site-${index}-addressTypeId`]}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-sky-900 mb-2">
                        Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={site.addressLine1}
                        onChange={(e) => setSiteValue(index, { addressLine1: e.target.value })}
                        className="w-full px-3 py-3 text-base border border-sky-200 rounded-lg"
                        placeholder="Enter Address"
                      />
                      {errors[`site-${index}-addressLine1`] && (
                        <p className="text-red-500 text-sm mt-1">{errors[`site-${index}-addressLine1`]}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-sky-900 mb-2">Country</label>
                      <select
                        value={site.countryId}
                        onChange={(e) => handleCountryChange(index, e.target.value)}
                        className="w-full px-3 py-3 text-base border border-sky-200 rounded-lg bg-white"
                      >
                        <option value="">Select Country</option>
                        {countries.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-sky-900 mb-2">State/Division</label>
                      <select
                        value={site.stateId}
                        onChange={(e) => handleStateChange(index, e.target.value)}
                        className="w-full px-3 py-3 text-base border border-sky-200 rounded-lg bg-white"
                      >
                        <option value="">Select State</option>
                        {site.stateList.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-sky-900 mb-2">District</label>
                      <select
                        value={site.districtId}
                        onChange={(e) => handleDistrictChange(index, e.target.value)}
                        className="w-full px-3 py-3 text-base border border-sky-200 rounded-lg bg-white"
                      >
                        <option value="">Select District</option>
                        {site.districtList.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-sky-900 mb-2">Thana</label>
                      <select
                        value={site.cityId}
                        onChange={(e) => setSiteValue(index, { cityId: e.target.value })}
                        className="w-full px-3 py-3 text-base border border-sky-200 rounded-lg bg-white"
                      >
                        <option value="">Select Thana</option>
                        {site.cityList.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-2 border-t border-gray-300 pt-4">
                      <div className="space-y-3">
                        {site.contactPersonInfos.map((person, contactIndex) => (
                          <div key={contactIndex} className="grid grid-cols-3 gap-3">
                            <input
                              value={person.firstName}
                              onChange={(e) =>
                                setContactValue(index, contactIndex, "firstName", e.target.value)
                              }
                              className="px-3 py-2 border border-sky-200 rounded"
                              placeholder="Contact First Name"
                            />
                            <input
                              value={person.lastName}
                              onChange={(e) =>
                                setContactValue(index, contactIndex, "lastName", e.target.value)
                              }
                              className="px-3 py-2 border border-sky-200 rounded"
                              placeholder="Contact Last Name"
                            />
                            <div className="flex gap-2">
                              <input
                                value={person.phoneNumber}
                                onChange={(e) =>
                                  setContactValue(
                                    index,
                                    contactIndex,
                                    "phoneNumber",
                                    formatPhone(e.target.value)
                                  )
                                }
                                className="flex-1 px-3 py-2 border border-sky-200 rounded"
                                placeholder="Contact Number"
                              />
                              <button
                                type="button"
                                className="px-2 text-red-500"
                                onClick={() => removeContactPerson(index, contactIndex)}
                              >
                                <i className="ri-delete-bin-line" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => addContactPerson(index)}
                        className="mt-3 px-4 py-1.5 border border-sky-500 text-sky-700 rounded-full text-sm"
                      >
                        Add Contact Person
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <div className="border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={addCustomerSite}
                  className="px-6 py-2 border border-sky-500 text-sky-700 rounded-full text-base font-semibold"
                >
                  Add Customer Contact Address
                </button>
              </div>
            </div>
          )}

          {submitError && <p className="text-red-600 text-sm">{submitError}</p>}
        </div>

        <div className="px-7 py-5 border-t border-gray-200 flex justify-end gap-3 bg-white">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-8 py-2.5 border border-sky-500 text-sky-700 rounded-full text-base font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-8 py-2.5 rounded-full text-base font-semibold ${
              isSubmitting
                ? "bg-gray-200 text-gray-500"
                : "bg-sky-700 hover:bg-sky-800 text-white"
            }`}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
